import { useRef } from 'react'

import styled from 'styled-components'

import Box from '../Box'
import Text from '../Text'
import IconButton from '../Buttons/IconButton'

export type NumberEventValue = {
  target: {
    name: string
    value: number | null
  }
}

export type NumberInputEvent = (event: NumberEventValue) => void

type Props = {
  name: string
  value?: number | null
  onChange?: NumberInputEvent
  onBlur?: NumberInputEvent
  step?: number
  min?: number
  max?: number
  decimalPlaces?: number
  width?: string
  textAlign?: 'left' | 'right' | 'center'
  isEditable?: boolean
  error?: string
}

const NumberInput = ({
  name,
  value,
  step = 1,
  min = 0,
  decimalPlaces = 0,
  max,
  onBlur,
  onChange,
  textAlign = 'center',
  isEditable = true,
  error,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const checkMinMax = (value: number) => {
    if (value < min) {
      return min
    }
    if (max && value > max) {
      return max
    }
    return value
  }
  const handleChange = (num: number | null, event?: NumberInputEvent) => {
    if (Number.isNaN(value) || num === null) {
      event?.({
        target: {
          name,
          value: null,
        },
      })
      return
    }

    const checkedValue = checkMinMax(num)
    const decimalPlacesValue = Math.pow(10, decimalPlaces)
    const roundedValue = Math.round(checkedValue * decimalPlacesValue) / decimalPlacesValue
    event?.({
      target: {
        name,
        value: roundedValue,
      },
    })
  }

  const handleUp = () => {
    inputRef.current?.focus()
    handleChange((value ?? 0) + step, onChange)
  }

  const handleDown = () => {
    inputRef.current?.focus()
    handleChange((value ?? 0) - step, onChange)
  }

  if (isEditable === false) {
    return <Text>{value}</Text>
  }

  return (
    <Box>
      <Container isError={!!error}>
        <Input
          ref={inputRef}
          type="number"
          value={value ?? undefined}
          onChange={(e) => {
            handleChange(e.target.value ? Number(e.target.value) : null, onChange)
          }}
          onBlur={(e) => {
            handleChange(e.target.value ? Number(e.target.value) : null, onBlur)
          }}
          textAlign={textAlign}
        />
        <ButtonContainer>
          <IconButton variant="ghost" iconColor="gray4" icon={['fas', 'chevron-up']} onClick={handleUp} />
          <IconButton variant="ghost" iconColor="gray4" icon={['fas', 'chevron-down']} onClick={handleDown} />
        </ButtonContainer>
      </Container>
      {error && (
        <Text variant="error" mt="0.2rem">
          {error}
        </Text>
      )}
    </Box>
  )
}

const Input = styled.input<{ textAlign: string }>`
  width: 100%;
  border: none;
  padding: 0.5rem;
  text-align: ${({ textAlign }) => textAlign};
`

const Container = styled.div<{ isError: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, isError }) => (isError ? theme.colors.red : theme.colors.defaultBorder)};
  border-radius: 4px;
`

const ButtonContainer = styled.div`
  border-left: 1px solid ${({ theme }) => theme.colors.defaultBorder};
`

export default NumberInput
